import { useCallbackRef } from '@synoptic/ui-kit/utils/use-callback-ref.js';
import { useEffect, useState } from 'react';
import { Location, useLocation } from 'react-router';
import { GridStateSnapshot, StateSnapshot } from 'react-virtuoso';

type FeedPositions =
  | Map<string, GridStateSnapshot>
  | Map<string, StateSnapshot>;

const defaultGetKey = (location: Location) => location.key;

export interface RestoreFeedBaseProps {
  getKey?: (location: Location) => string;
  onRestore?: (args: { fetchOptions: Partial<Request> }) => void;
}

const getMinHeight = (restoreStateFrom: StateSnapshot | GridStateSnapshot) => {
  if ('ranges' in restoreStateFrom) {
    return restoreStateFrom.ranges.reduce(
      (acc, v) =>
        v.endIndex === Infinity
          ? acc + v.size
          : acc + v.size * (v.endIndex - v.startIndex),
      0,
    );
  }

  return restoreStateFrom.viewport.height + restoreStateFrom.scrollTop;
};
export const useRestoreFeedBase = ({
  feedPositions,
  getKey = defaultGetKey,
  onRestore,
}: RestoreFeedBaseProps & { feedPositions: FeedPositions }) => {
  const loc = useLocation();
  const [minHeightLock, setMinHeightLock] = useState(true);

  const key = getKey(loc);

  const [restoreStateFrom] = useState(() => feedPositions.get(key));

  const refresh = useCallbackRef(onRestore);

  useEffect(() => {
    if (feedPositions.get(key)) {
      const abortController = new AbortController();

      refresh({ fetchOptions: { signal: abortController.signal } });

      return () => {
        abortController.abort();
      };
    }
  }, [key, refresh, feedPositions]);

  return {
    restoreStateFrom,
    key,

    /** pass this to feed container to avoid flash of unscrolled content https://github.com/petyosi/react-virtuoso/issues/957#issuecomment-1653179355 */
    // TODO: might be better to calculate correct initialItemCount based on restoreStateFrom
    style:
      restoreStateFrom && minHeightLock
        ? { minHeight: getMinHeight(restoreStateFrom) }
        : undefined,
    totalListHeightChanged: () => setMinHeightLock(false),
  };
};
